import { useEffect, useState } from 'react';
import AdminSidebar from '../components/AdminSidebar';

const API_BASE = process.env.REACT_APP_API_BASE_URL || `${process.env.FRONTEND_URL}/api`;

const STATUS_STYLES = {
  CONFIRMED: 'bg-green-100 text-green-700',
  PENDING_REVIEW: 'bg-amber-100 text-amber-700',
  REJECTED: 'bg-red-100 text-red-700',
};

export default function AdminPaymentsPage() {
  const [payments, setPayments] = useState([]);
  const [meta, setMeta] = useState({ totalCollected: 0, pendingCount: 0, successRate: '0.0' });
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [method, setMethod] = useState('');
  const [status, setStatus] = useState('');

  const token = localStorage.getItem('adminToken');

  const fetchPayments = () => {
    setLoading(true);
    const params = new URLSearchParams();
    if (search) params.set('search', search);
    if (method) params.set('method', method);
    if (status) params.set('status', status);
    fetch(`${API_BASE}/admin/payments?${params.toString()}`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => r.json())
      .then((json) => {
        if (json.success) {
          setPayments(json.data);
          setMeta(json.meta);
        }
      })
      .finally(() => setLoading(false));
  };

  useEffect(fetchPayments, [search, method, status]);

  const handleConfirm = async (id) => {
    await fetch(`${API_BASE}/admin/payments/${id}/confirm`, { method: 'PATCH', headers: { Authorization: `Bearer ${token}` } });
    fetchPayments();
  };

  const handleReject = async (id) => {
    await fetch(`${API_BASE}/admin/payments/${id}/reject`, { method: 'PATCH', headers: { Authorization: `Bearer ${token}` } });
    fetchPayments();
  };

  return (
    <div className="bg-[#f7f9fb] font-['Plus_Jakarta_Sans'] min-h-screen">
      <AdminSidebar />
      <main className="pt-16 md:pt-2 md:ml-64 min-h-screen p-8">
        <h2 className="text-3xl font-bold text-[#00255a] mb-1">Payments Log</h2>
        <p className="text-[#64748b] mb-8">Manual payment tracking and confirmation.</p>

        <section className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-6">
          <div className="bg-white p-6 rounded-xl border border-[#e2e8f0] shadow-sm">
            <span className="inline-block p-2 bg-[#00255a]/5 text-[#00255a] rounded-lg mb-4">
              <span className="material-symbols-outlined">payments</span>
            </span>
            <p className="text-[#64748b] text-xs uppercase font-bold">Total Collected</p>
            <h3 className="text-2xl font-bold text-[#00255a] mt-1">${meta.totalCollected.toLocaleString()}</h3>
          </div>
          <div className="bg-white p-6 rounded-xl border border-[#e2e8f0] shadow-sm">
            <span className="inline-block p-2 bg-[#365da3]/5 text-[#365da3] rounded-lg mb-4">
              <span className="material-symbols-outlined">pending_actions</span>
            </span>
            <p className="text-[#64748b] text-xs uppercase font-bold">Pending Review</p>
            <h3 className="text-2xl font-bold text-[#00255a] mt-1">{meta.pendingCount} Payments</h3>
          </div>
          <div className="bg-white p-6 rounded-xl border border-[#e2e8f0] shadow-sm">
            <span className="inline-block p-2 bg-[#79161e]/5 text-[#862026] rounded-lg mb-4">
              <span className="material-symbols-outlined">account_balance_wallet</span>
            </span>
            <p className="text-[#64748b] text-xs uppercase font-bold">This Month</p>
            <h3 className="text-2xl font-bold text-[#00255a] mt-1">{payments.length} Records</h3>
          </div>
          <div
            className="p-6 rounded-xl text-white shadow-lg flex flex-col justify-between"
            style={{ background: 'linear-gradient(135deg, #1e3c72 0%, #2a5298 60%, #ff7b7b 100%)' }}
          >
            <span className="inline-block p-2 bg-white/20 rounded-lg mb-4 w-fit">
              <span className="material-symbols-outlined">verified</span>
            </span>
            <p className="text-xs uppercase font-bold opacity-80">Success Rate</p>
            <h3 className="text-2xl font-bold mt-1">{meta.successRate}%</h3>
          </div>
        </section>

        <section className="bg-white p-4 rounded-xl border border-[#e2e8f0] mb-6 flex flex-wrap items-center gap-4 shadow-sm">
          <div className="flex-1 min-w-[200px] relative">
            <span className="absolute left-3 top-1/2 -translate-y-1/2 material-symbols-outlined text-[#747781]">search</span>
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search applicant..."
              className="w-full pl-10 pr-4 py-2 rounded-lg border border-[#e2e8f0] focus:ring-2 focus:ring-[#00255a]/10 outline-none"
            />
          </div>
          <select value={method} onChange={(e) => setMethod(e.target.value)} className="border border-[#e2e8f0] rounded-lg px-3 py-2 outline-none bg-white">
            <option value="">All Methods</option>
            <option value="Bank Transfer">Bank Transfer</option>
            <option value="Mobile Money">Mobile Money</option>
            <option value="Cash">Cash</option>
          </select>
          <select value={status} onChange={(e) => setStatus(e.target.value)} className="border border-[#e2e8f0] rounded-lg px-3 py-2 outline-none bg-white">
            <option value="">All Status</option>
            <option value="CONFIRMED">Confirmed</option>
            <option value="PENDING_REVIEW">Pending Review</option>
            <option value="REJECTED">Rejected</option>
          </select>
        </section>

        <section className="bg-white rounded-xl border border-[#e2e8f0] overflow-hidden shadow-sm">
          <table className="w-full text-left border-collapse">
            <thead className="bg-[#f2f4f6] border-b border-[#e2e8f0]">
              <tr>
                {['Applicant', 'Type & Label', 'Amount', 'Method', 'Proof', 'Date & Staff', 'Status', ''].map((h) => (
                  <th key={h} className="px-6 py-4 text-[#00255a] text-xs uppercase">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-[#e2e8f0]">
              {loading ? (
                <tr><td colSpan="8" className="p-12 text-center text-[#64748b]">Loading...</td></tr>
              ) : payments.length === 0 ? (
                <tr><td colSpan="8" className="p-12 text-center text-[#64748b]">No payments found.</td></tr>
              ) : (
                payments.map((p) => (
                  <tr key={p.id} className="hover:bg-[#f7f9fb] transition-colors group">
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-[#8fb3ff]/20 flex items-center justify-center text-[#365da3] font-bold">
                          {p.application?.applicant?.fullName?.charAt(0)}
                        </div>
                        <div>
                          <p className="text-[#191c1e] font-semibold">{p.application?.applicant?.fullName}</p>
                          <p className="text-xs text-[#64748b]">ID: {p.applicationId?.slice(0, 8).toUpperCase()}</p>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <p className="text-[#191c1e]">{p.application?.opportunity?.title || 'General'}</p>
                      <span className="text-[10px] font-bold uppercase text-[#00255a]/60">{p.label}</span>
                    </td>
                    <td className="px-6 py-4"><p className="font-bold text-[#00255a]">{p.currency} {Number(p.amount).toLocaleString()}</p></td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2 text-sm text-[#64748b]">
                        <span className="material-symbols-outlined text-sm">account_balance</span>
                        {p.method || '—'}
                      </div>
                    </td>
                    <td className="px-6 py-4 text-center">
                      {p.proofUrl ? (
                        <a href={p.proofUrl} target="_blank" rel="noreferrer" className="relative inline-block group/thumb cursor-pointer">
                          <div
                            className="w-12 h-12 rounded-lg bg-cover bg-center border border-[#e2e8f0] shadow-sm group-hover/thumb:scale-110 transition-transform"
                            style={{ backgroundImage: `url(${p.proofUrl})` }}
                          />
                        </a>
                      ) : <span className="text-[#c4c6d1]">—</span>}
                    </td>
                    <td className="px-6 py-4">
                      <p className="text-[#191c1e] text-sm">{new Date(p.createdAt).toLocaleDateString()}</p>
                      <p className="text-xs text-[#64748b]">Marked by: {p.markedBy?.fullName || '—'}</p>
                    </td>
                    <td className="px-6 py-4">
                      <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold uppercase ${STATUS_STYLES[p.status]}`}>
                        {p.status.replace('_', ' ')}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-right whitespace-nowrap">
                      {p.status === 'PENDING_REVIEW' && (
                        <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                          <button onClick={() => handleConfirm(p.id)} className="p-2 text-emerald-600 hover:bg-emerald-50 rounded-full" title="Confirm">
                            <span className="material-symbols-outlined text-[18px]">check_circle</span>
                          </button>
                          <button onClick={() => handleReject(p.id)} className="p-2 text-red-500 hover:bg-red-50 rounded-full" title="Reject">
                            <span className="material-symbols-outlined text-[18px]">cancel</span>
                          </button>
                        </div>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </section>
      </main>
    </div>
  );
}